import { THEMES } from '@cmctechnology/webinvest-constants';
import {
  BaseDiv,
  IGapProps,
  useColumnGapCss,
  Heading1,
  P,
  Checkbox,
  Label,
  Select,
  Button,
  ErrorText,
  GapContainer,
  Span,
  ModuleContainer,
  Heading3
} from '@cmctechnology/webinvest-design-system';
import type { SingleValue } from 'react-select';
import { useFrontendContext } from '@cmctechnology/webinvest-store-frontend';
import { frontendActions } from '@cmctechnology/webinvest-store-frontend';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { IStore } from '../store/store';
import { useNavigate } from 'react-router';

const PageContainer = styled(BaseDiv)`
  margin: 1.4rem 2rem;
`;

const ModuleContainerStyled = styled(ModuleContainer)`
  flex: 1 1 0;
  width: 100%;
  padding: 2rem;
`;

const ButtonContainer = styled(BaseDiv)`
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  width: 25rem;
`;

const ColsContainer = styled(GapContainer)<IGapProps>`
  ${useColumnGapCss()};
  word-wrap: normal;
`;
type Option = {
  label: string;
  value: string;
};
const SUBCRIBE_PRO_API_NAME = 'ClientProSubscriptionUpdate1';

type SubscriptionUpdateStatus = 'in-progress' | 'success' | 'error' | undefined;

export const ProSubscription = () => {
  const { t } = useTranslation();
  const { theme, config } = useFrontendContext();
  const allAccounts = useSelector((store: IStore) => store.accounts.account);
  const [subscriptionUpdateStatus, setSubscriptionUpdateStatus] = useState<SubscriptionUpdateStatus>(undefined);
  const navigate = useNavigate();

  const accountOptions = Object.values(allAccounts).map((account) => ({
    label: `${account?.number}: ${account?.name}`,
    value: account?.number
  }));

  const fsgUrl =
    theme === THEMES.StGeorge
      ? 'https://www.stgeorge.com.au/content/dam/stg/downloads/invest/Directshares-Financial-Services-Guide.pdf'
      : 'https://www.cmcmarkets.com/en-au/legal-documents/stockbroking-legal-documents';

  const [agreement, setAgreement] = useState(false);
  const [selectedAccountOption, setSelectedAccountOption] = useState<Option | undefined>(accountOptions.length === 1 ? accountOptions[0] : undefined);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Handle subscription logic here
    if (agreement && selectedAccountOption?.value) {
      // Call the API to subscribe the user to Pro platform
      // Redirect to Pro platform URL after successful subscription
      const requestBody = {
        FormData: [
          {
            Name: 'cmcsb-intercept-user-will-pay',
            Value: 'on'
          },
          {
            Name: 'cmcsb-charge-account-number',
            Value: selectedAccountOption.value
          }
        ]
      };
      try {
        setSubscriptionUpdateStatus('in-progress');

        const confirmationResponse = await frontendActions.webTradingApiCallDirect(SUBCRIBE_PRO_API_NAME, requestBody, true, true, true);
        const successResponse = confirmationResponse as { Success: boolean; UiMessage: string | null };

        if (successResponse.Success) {
          setSubscriptionUpdateStatus('success');
          window.open(config.proUrl);
          navigate('/platforms');
          window.location.reload();
        } else {
          setSubscriptionUpdateStatus('error');
        }
      } catch (error) {
        console.error('Error subscribing to Pro platform:', error);
        setSubscriptionUpdateStatus('error');
        // Handle error (e.g., show a notification to the user)
      }
    }
  };

  const handleCancel = () => {
    navigate('/platforms');
    window.location.reload();
  };

  return (
    <PageContainer>
      <Heading1 headingType='SEMI_BOLD'>{t('Platform')}</Heading1>
      <ModuleContainerStyled>
        <Heading3 headingType='SEMI_BOLD'>{t('Subscribe to the Pro platform')}</Heading3>
        <P gutterBottom={true}>
          {t(
            `You can subscribe to CMC Invest Pro to gain access to trading features such as advanced charting, in-depth technical analysis tools, customisable layouts and more. With this subscription, you will also receive complimentary access to our ASX Dynamic Market Data package to further enhance your trading efficiency.`
          )}
          {t(` The cost of the subscription is `)}
          <Span textStyle='MEDIUM'>{t(`$49.00 per calendar month or part thereof (including GST). `)}</Span>
          {t('Please read the ')}
          <a href={fsgUrl} target='_blank' rel='noreferrer'>
            {t(`Financial Service Guide (FSG)`)}
          </a>
          {t(` for more information on the monthly fees associated with this subscription.`)}
        </P>

        <form onSubmit={handleFormSubmit}>
          <ColsContainer gap={2} alignItems='flex-start'>
            <Checkbox
              checked={agreement}
              size='xSmall'
              label={t('I have read the Financial Service Guide (FSG) and agree to the monthly subscription fee for the Pro platform')}
              onChange={() => {
                setAgreement(!agreement);
              }}
              gutterBottom={true}
              required
            />
            {accountOptions.length > 1 && (
              <>
                <Label htmlFor='accountId'>{t('Please select account to be billed:')}</Label>
                <Select
                  id='accountId'
                  value={selectedAccountOption}
                  options={accountOptions}
                  onChange={(option: SingleValue<Option>) => {
                    /* istanbul ignore next */
                    if (!option) return;
                    setSelectedAccountOption(option);
                  }}
                  width='large'
                  required
                  isSearchable={false}
                ></Select>
              </>
            )}
            {subscriptionUpdateStatus === 'error' && <ErrorText>{t('There was an error processing your subscription. Please try again later.')}</ErrorText>}
            <ButtonContainer>
              <Button size='medium' variant='primary' type='submit' processing={subscriptionUpdateStatus === 'in-progress'}>
                {t('Subscribe & launch Pro platform')}
              </Button>
              <Button size='medium' type='button' onClick={handleCancel}>
                {t('Cancel')}
              </Button>
            </ButtonContainer>
          </ColsContainer>
        </form>
      </ModuleContainerStyled>
    </PageContainer>
  );
};
